import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "next-themes";

import { cn } from "@/lib/utils";
import { formatAmount, formatNumber } from "@/utils/formatters";
import type { DashboardCountry } from "@/types/dashboard";
import { MAP_H, MAP_W, baseMapSvg, placeCountries, type MapCountry } from "./country-geometry";

/* The app's country list uses a few codes the flag set spells differently. */
const FLAG_CODE_ALIASES: Record<string, string> = {
  uk: "gb",
  "eu.int": "eu",
};

export function Flag({ code, className }: { code: string; className?: string }) {
  if (!code || code === "Unknown") return null;
  const lower = code.toLowerCase();
  const flag = FLAG_CODE_ALIASES[lower] ?? lower;
  return <span aria-hidden className={cn("fi rounded-[2px]", `fi-${flag}`, className)} />;
}

/** Where a dot sits on screen, so the tooltip can be placed outside the card. */
interface Hover {
  country: MapCountry;
  cx: number;
  top: number;
  bottom: number;
}

const GAP = 8;
const VIEWPORT_MARGIN = 8;

/**
 * Affiliates by country. Circle area follows the affiliate count and is
 * capped by the country's own footprint, so a crowded small country never
 * spills over its neighbours. Hover shows that country's period figures in a
 * static tooltip rendered on the document body, so it can extend past the
 * card and is only clamped to the viewport.
 */
export function AffiliateMap({
  countries,
  className,
  onSelect,
}: {
  countries: DashboardCountry[];
  className?: string;
  onSelect?: (country: DashboardCountry) => void;
}) {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  const svg = useMemo(() => baseMapSvg(dark), [dark]);
  const placed = useMemo(() => placeCountries(countries), [countries]);
  const [hover, setHover] = useState<Hover | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(null);

  // Place the panel above the dot when there is room, else below, and keep
  // it inside the viewport horizontally.
  useLayoutEffect(() => {
    if (!hover || !tipRef.current) {
      setTipPos(null);
      return;
    }
    const { width, height } = tipRef.current.getBoundingClientRect();
    const above = hover.top - GAP - height >= VIEWPORT_MARGIN;
    const top = above ? hover.top - GAP - height : hover.bottom + GAP;
    const left = Math.min(
      Math.max(hover.cx - width / 2, VIEWPORT_MARGIN),
      window.innerWidth - width - VIEWPORT_MARGIN
    );
    setTipPos({ left, top });
  }, [hover]);

  const show = (c: MapCountry, target: Element) => {
    const rect = target.getBoundingClientRect();
    setHover({ country: c, cx: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom });
  };

  const hovered = hover?.country ?? null;

  return (
    <div
      className={cn("relative w-full select-none", className)}
      // The land image and the circle layer share this box, so they align
      // at every width.
      style={{ aspectRatio: `${MAP_W} / ${MAP_H}` }}
    >
      <img
        src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
        alt=""
        draggable={false}
        className="pointer-events-none block h-full w-full [mask-image:linear-gradient(to_bottom,transparent,white_8%,white_92%,transparent)]"
      />
      <svg
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        role="img"
        aria-label="Affiliates by country"
      >
        {placed.map((c) => {
          const active = hovered?.country === c.country;
          return (
            <g key={c.country}>
              <circle
                cx={c.x}
                cy={c.y}
                r={c.r}
                fill="rgb(var(--brand-600))"
                fillOpacity={active ? 0.55 : 0.3}
                stroke="rgb(var(--brand-600))"
                strokeWidth={0.35}
              />
              {/* A wider invisible target so tiny dots are easy to hit. */}
              <circle
                cx={c.x}
                cy={c.y}
                r={Math.max(c.r, 2.2)}
                fill="transparent"
                className={onSelect ? "cursor-pointer" : "cursor-default"}
                onMouseEnter={(e) => show(c, e.currentTarget)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelect?.(c)}
              >
                <title>{`${c.name}: ${formatNumber(c.affiliates)} affiliates`}</title>
              </circle>
            </g>
          );
        })}
      </svg>

      {hovered &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            className="pointer-events-none fixed z-[70] w-max max-w-[220px] rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
            style={{
              left: tipPos?.left ?? -9999,
              top: tipPos?.top ?? -9999,
              visibility: tipPos ? "visible" : "hidden",
            }}
          >
            <div className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold">
              <Flag code={hovered.country} />
              <span className="truncate">{hovered.name}</span>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 tabular-nums">
              <dt className="text-muted-foreground">Affiliates</dt>
              <dd className="text-right">{formatNumber(hovered.affiliates)}</dd>
              <dt className="text-muted-foreground">Selling</dt>
              <dd className="text-right">{formatNumber(hovered.sellers)}</dd>
              <dt className="text-muted-foreground">Orders</dt>
              <dd className="text-right">{formatNumber(hovered.orders)}</dd>
              <dt className="text-muted-foreground">Revenue</dt>
              <dd className="text-right">{formatAmount(hovered.revenue)}</dd>
            </dl>
            {hovered.topProduct && (
              <div className="mt-1 border-t border-border pt-1 text-muted-foreground">
                Top product:{" "}
                <span className="text-popover-foreground">{hovered.topProduct.title}</span>
                {" "}({formatNumber(hovered.topProduct.units)})
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
